import React, { useState, useRef } from 'react';
import { Music, Globe, Heart, Award, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { podcastInfo } from '../data/mockData';

const galleryImages = [
  {
    src: "https://customer-assets.emergentagent.com/job_6efdf00b-2eb4-4fd8-a5de-0bea7cc4b344/artifacts/ecd86fyz_Screen%20Shot%202026-03-15%20at%204.00.03%20PM.png",
    alt: "Naketa performing",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_6efdf00b-2eb4-4fd8-a5de-0bea7cc4b344/artifacts/adbedxbj_Screen%20Shot%202026-03-15%20at%204.00.44%20PM.png",
    alt: "Naketa with dolphin",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_6efdf00b-2eb4-4fd8-a5de-0bea7cc4b344/artifacts/pus27gql_Screen%20Shot%202026-03-15%20at%204.01.24%20PM.png",
    alt: "Naketa with friends",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e83aa675-ca05-428d-9fea-ff8ac20fa270/artifacts/nea18tw5_Screen%20Shot%202026-03-15%20at%203.59.17%20PM.png",
    alt: "Naketa smiling",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e83aa675-ca05-428d-9fea-ff8ac20fa270/artifacts/9kjzg5ff_Screen%20Shot%202026-03-15%20at%205.05.58%20PM.png",
    alt: "Naketa glamour",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e83aa675-ca05-428d-9fea-ff8ac20fa270/artifacts/siudz9s7_Screen%20Shot%202026-03-16%20at%2010.47.20%20AM.png",
    alt: "Naketa leopard print",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e83aa675-ca05-428d-9fea-ff8ac20fa270/artifacts/9unb0l6e_Screen%20Shot%202026-03-16%20at%2010.48.12%20AM.png",
    alt: "Naketa in the studio",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e83aa675-ca05-428d-9fea-ff8ac20fa270/artifacts/ier78idv_Screen%20Shot%202026-03-16%20at%2010.49.08%20AM.png",
    alt: "Naketa blue backdrop",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_e83aa675-ca05-428d-9fea-ff8ac20fa270/artifacts/y5rrrj01_Screen%20Shot%202026-03-16%20at%2010.50.00%20AM.png",
    alt: "Naketa pink polka dot",
  },
];

const allImages = [
  ...galleryImages,
  {
    src: "https://customer-assets.emergentagent.com/job_6efdf00b-2eb4-4fd8-a5de-0bea7cc4b344/artifacts/wbo8kzze_facebook_1773603428874_7439031956142615394.jpg",
    alt: "Naketa in studio",
  },
];

const About = () => {
  const [lightbox, setLightbox] = useState({ open: false, index: 0 });

  const openLightbox = (index) => setLightbox({ open: true, index });
  const closeLightbox = () => setLightbox({ open: false, index: 0 });
  const prevImage = () => setLightbox(s => ({ ...s, index: (s.index - 1 + allImages.length) % allImages.length }));
  const nextImage = () => setLightbox(s => ({ ...s, index: (s.index + 1) % allImages.length }));

  const GalleryCard = ({ src, alt, index, className, style, imgStyle }) => {
    const cardRef = useRef(null);
    const handleMouseMove = (e) => {
      const card = cardRef.current;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      card.style.transform = `perspective(800px) rotateY(${x * 8}deg) rotateX(${-y * 8}deg) scale(1.02)`;
    };
    const handleMouseLeave = (e) => {
      if (cardRef.current) cardRef.current.style.transform = '';
    };
    return (
      <div ref={cardRef}
        className={`relative group cursor-pointer overflow-hidden rounded-2xl transition-transform duration-300 ${className || ''}`}
        style={{ ...style, willChange: 'transform' }}
        onClick={() => openLightbox(index)}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}>
        <img src={src} alt={alt}
          className="w-full h-full object-cover gallery-img-sharp transition-all duration-700 group-hover:scale-110"
          style={imgStyle} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-400 pointer-events-none" />
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-all duration-300 bg-purple-600/70 backdrop-blur-sm rounded-full p-2 pointer-events-none">
          <ZoomIn className="w-4 h-4 text-white" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-500 pointer-events-none">
          <span className="text-white font-semibold text-sm drop-shadow-lg" style={{ fontFamily: "'Prata', serif" }}>{alt}</span>
        </div>
        <div className="absolute inset-0 rounded-2xl border-2 border-purple-500/0 group-hover:border-purple-400/60 group-hover:shadow-[0_0_20px_rgba(168,85,247,0.3)] transition-all duration-500 pointer-events-none" />
      </div>
    );
  };

  const highlights = [
    { icon: Music, title: '30+ Songs Recorded', description: 'Including hits like Summertime, Push It On Me, and Independence' },
    { icon: Globe, title: 'International Performer', description: 'Performances across US, Canada, Italy, Africa, and Caribbean' },
    { icon: Award, title: 'Multi-Talented Artist', description: 'Singer, songwriter, dancer, actress, poet, and entrepreneur' },
    { icon: Heart, title: 'Community Advocate', description: 'Dedicated to youth development and arts education' }
  ];

  return (
    <section id="about" className="py-24 bg-gradient-to-b from-black via-gray-950 to-black relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16" data-testid="about-header">
          <h2 className="hero-title-text text-5xl md:text-6xl mb-4" style={{ WebkitTextStroke: '1px #FFD700' }}>
            Meet the Host
          </h2>
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
            <div className="h-2 w-2 bg-purple-500 rounded-full animate-pulse"></div>
            <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-purple-500 to-transparent"></div>
          </div>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto" style={{ fontFamily: "'Prata', serif" }}>
            A Versatile Voice in Reggae and the Diaspora Entertainment Scene
          </p>
        </div>

        {/* Main Content — Hero image + Bio */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="relative group" data-testid="about-main-image">
            <div className="absolute inset-0 bg-purple-600 rounded-2xl blur-2xl opacity-30 group-hover:opacity-50 transition-opacity duration-300"></div>
            <div className="relative overflow-hidden rounded-2xl border-2 border-purple-500/40">
              <img
                src="https://customer-assets.emergentagent.com/job_6efdf00b-2eb4-4fd8-a5de-0bea7cc4b344/artifacts/x0lx6ctu_Meet%20the%20Host.png"
                alt={podcastInfo.host.name}
                className="w-full h-auto object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-300 mb-2"
                style={{ fontFamily: "'Abril Fatface', serif" }}>{podcastInfo.host.name}</h3>
              <p className="text-xl text-purple-400 mb-6 font-bold" style={{ fontFamily: "'Prata', serif" }}>{podcastInfo.host.title}</p>
            </div>
            <p className="text-gray-300 leading-relaxed text-lg">{podcastInfo.host.bio}</p>
            <div className="space-y-4 pt-4">
              {podcastInfo.host.fullBio.slice(0, 3).map((paragraph, index) => (
                <p key={index} className="text-gray-400 leading-relaxed">{paragraph}</p>
              ))}
            </div>
          </div>
        </div>

        {/* Photo Gallery — Editorial Collage */}
        <div className="mb-16" data-testid="about-gallery">
          <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-300 mb-10 text-center"
            style={{ fontFamily: "'Abril Fatface', serif" }}>Life & Vibes</h3>

          {/* Row 1: Hero image + 2 stacked */}
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <GalleryCard src={galleryImages[0].src} alt={galleryImages[0].alt} index={0}
              className="md:w-3/5" style={{ aspectRatio: '4/3' }} />
            <div className="md:w-2/5 flex flex-col gap-4">
              <GalleryCard src={galleryImages[1].src} alt={galleryImages[1].alt} index={1}
                className="flex-1" />
              <GalleryCard src={galleryImages[2].src} alt={galleryImages[2].alt} index={2}
                className="flex-1" />
            </div>
          </div>

          {/* Row 2: 3 portrait columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <GalleryCard src={galleryImages[3].src} alt={galleryImages[3].alt} index={3}
              style={{ aspectRatio: '3/4' }} />
            <GalleryCard src={galleryImages[4].src} alt={galleryImages[4].alt} index={4}
              style={{ aspectRatio: '3/4' }} />
            <GalleryCard src={galleryImages[5].src} alt={galleryImages[5].alt} index={5}
              className="col-span-2 md:col-span-1" style={{ aspectRatio: '3/4' }} />
          </div>

          {/* Row 3: 2 stacked left + large right */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="md:w-2/5 flex flex-col gap-4">
              <GalleryCard src={galleryImages[6].src} alt={galleryImages[6].alt} index={6}
                className="flex-1" />
              <GalleryCard src={galleryImages[7].src} alt={galleryImages[7].alt} index={7}
                className="flex-1" />
            </div>
            <GalleryCard src={galleryImages[8].src} alt={galleryImages[8].alt} index={8}
              className="md:w-3/5" style={{ aspectRatio: '4/3' }} />
          </div>
        </div>

        {/* Highlights Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-16" data-testid="about-highlights">
          {highlights.map((item, index) => {
            const Icon = item.icon;
            return (
              <div key={index}
                className="group bg-gray-950 border border-purple-500/20 rounded-xl p-6 text-center hover:border-purple-500/50 transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-purple-500/20">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-900/30 rounded-full mb-4 group-hover:bg-purple-900/50 transition-colors">
                  <Icon className="h-8 w-8 text-purple-400" />
                </div>
                <h4 className="text-white font-bold mb-2">{item.title}</h4>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </div>
            );
          })}
        </div>

        {/* Extended Bio */}
        <div className="mt-16 bg-gray-950 border border-purple-500/20 rounded-2xl p-8 shadow-xl shadow-purple-500/10" data-testid="about-extended-bio">
          <h4 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-300 mb-6"
            style={{ fontFamily: "'Abril Fatface', serif" }}>The Journey Continues</h4>
          <div className="space-y-4 text-gray-300 leading-relaxed">
            {podcastInfo.host.fullBio.slice(3).map((paragraph, index) => (
              <p key={index}>{paragraph}</p>
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightbox.open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
          data-testid="lightbox-modal"
          onClick={closeLightbox}>
          <button data-testid="lightbox-close" onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 text-white/80 hover:text-white transition-colors">
            <X className="h-8 w-8" />
          </button>
          <button data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 z-10 text-white/60 hover:text-white transition-colors">
            <ChevronLeft className="h-10 w-10" />
          </button>
          <button data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 z-10 text-white/60 hover:text-white transition-colors">
            <ChevronRight className="h-10 w-10" />
          </button>
          <img
            src={allImages[lightbox.index].src}
            alt={allImages[lightbox.index].alt}
            className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 text-white/50 text-sm">
            {lightbox.index + 1} / {allImages.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default About;
